import React from 'react';
import { Icon } from '@iconify/react';
import { useTranslation } from 'react-i18next';
import { useTheme } from '../../../contexts/ThemeContext';
import { useDirection } from '../../../contexts/DirectionContext';

export type BadgeType = 'live' | 'upcoming' | 'new';

interface BadgeProps {
  type: BadgeType;
  className?: string;
  size?: 'default' | 'large';
}

export const Badge: React.FC<BadgeProps> = ({ type, className = '', size = 'default' }) => {
  const { t } = useTranslation();
  const { isDark } = useTheme();
  const { isRTL } = useDirection();

  const getBadgeStyles = () => {
    switch (type) {
      case 'live':
        return {
          backgroundColor: 'rgba(var(--colors-status-error-rgb), 0.6)',
          icon: (
            <div className={`${size === 'large' ? 'w-2.5 h-2.5' : 'w-1.5 h-1.5'} rounded-full ${isDark ? 'bg-text-primary' : 'bg-text-inverse'} animate-pulse`} />
          ),
          text: t('common.live')
        };
      case 'upcoming':
        return {
          backgroundColor: 'rgba(var(--colors-status-warning-rgb), 0.6)',
          icon: <Icon icon="ph:broadcast" className={size === 'large' ? 'w-5 h-5' : 'w-3.5 h-3.5'} />,
          text: t('common.upcoming')
        };
      case 'new':
        return {
          backgroundColor: 'rgba(var(--colors-status-success-rgb), 0.6)',
          icon: null,
          text: t('common.new')
        };
    }
  };

  const styles = getBadgeStyles();
  const sizeClasses = size === 'large' ? 'px-4 py-1.5 text-sm gap-2' : 'px-2 py-1 text-xs gap-1.5';

  return (
    <div 
      style={{ backgroundColor: styles.backgroundColor }}
      className={`
        absolute top-2 ${isRTL ? 'end-2' : 'start-2'}
        ${sizeClasses}
        rounded-full
        backdrop-blur-sm
        ${isDark ? 'text-text-primary' : 'text-text-inverse'} font-medium
        shadow-lg
        z-20
        flex items-center
        ${className}
      `}
    >
      {styles.icon}
      {styles.text}
    </div>
  );
};

export default Badge; 